import { z } from 'zod';

// Validation message keys — passed in from the component so copy lives in
// en.yml (order_details.create_event.errors.*). The English defaults below are
// fallbacks for tests / non-React callers only.
export interface CreateEventValidationMessages {
  formatRequired: string;
  productLineRequired: string;
  guestCountRequired: string;
  servingLayoutRequired: string;
  servingTimeRequired: string;
  servingDateRequired: string;
}

export const DEFAULT_CREATE_EVENT_VALIDATION_MESSAGES: CreateEventValidationMessages = {
  formatRequired: 'Please select a format',
  productLineRequired: 'Please select a product line',
  guestCountRequired: 'Guest count must be greater than 0',
  servingLayoutRequired: 'Enter lines and ways',
  servingTimeRequired: 'Please select a serving time',
  servingDateRequired: 'Please select a serving date',
};

// One event row in the modal. The base object types every EventFormState key so
// `z.infer` mirrors the form state exactly; no per-field `.min` lives here —
// requiredness is enforced in the wrapper's `.superRefine` so blank extra events
// (added then left untouched) never block the submit, matching the submit-time
// `filter(form !== '')`.
const eventShape = z.object({
  id: z.number(),
  form: z.string(),
  productLine: z.string(),
  paxCount: z.number(),
  lines: z.number(),
  ways: z.number(),
  tags: z.array(z.string()),
  scheduledAt: z.date().optional(),
  servingTime: z.string(),
  description: z.string(),
  specialInstructions: z.string(),
  expanded: z.boolean(),
  disabledFields: z.array(z.string()).optional(),
});

type EventShape = z.infer<typeof eventShape>;

// A "started" event is one the rep has begun (a format is picked). Only started
// events are validated and submitted — the shared invariant behind both the
// schema's target selection and the dialog's submit-time filter.
export function isStartedEvent(event: Pick<EventShape, 'form'>): boolean {
  return event.form !== '';
}

export function makeCreateEventSchema(messages?: Partial<CreateEventValidationMessages>) {
  const m = { ...DEFAULT_CREATE_EVENT_VALIDATION_MESSAGES, ...messages };

  return z
    .object({
      events: z.array(eventShape),
    })
    .superRefine((val, ctx) => {
      // Only started events are submitted; validate those. If none is started,
      // validate the first row so the empty modal still surfaces "select a
      // format" rather than submitting nothing.
      const started = val.events
        .map((event, index) => ({ event, index }))
        .filter(({ event }) => isStartedEvent(event));
      const targets = started.length > 0
        ? started
        : val.events.length > 0
          ? [{ event: val.events[0], index: 0 }]
          : [];

      for (const { event, index } of targets) {
        const disabled = (key: string) => event.disabledFields?.includes(key) ?? false;
        const addIssue = (key: string, message: string) =>
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['events', index, key], message });

        if (event.form === '') addIssue('form', m.formatRequired);
        if (!disabled('productLine') && event.productLine === '') {
          addIssue('productLine', m.productLineRequired);
        }
        if (!disabled('paxCount') && !(event.paxCount > 0)) {
          addIssue('paxCount', m.guestCountRequired);
        }
        // Lines + Ways are one control the form locks if EITHER key is disabled;
        // skip the pair's check (single message, anchored on `ways`) when locked.
        const layoutLocked = disabled('lines') || disabled('ways');
        if ((!event.form || event.form === 'buffet') && !layoutLocked && (!(event.lines > 0) || !(event.ways > 0))) {
          addIssue('ways', m.servingLayoutRequired);
        }
        if (!disabled('servingTime') && event.servingTime === '') {
          addIssue('servingTime', m.servingTimeRequired);
        }
        if (!disabled('scheduledAt') && !event.scheduledAt) {
          addIssue('scheduledAt', m.servingDateRequired);
        }
      }
    });
}

// Static fallback for tests / non-React callers.
export const createEventSchema = makeCreateEventSchema();

export type CreateEventFormData = z.infer<typeof createEventSchema>;
export type CreateEventFormEvent = EventShape;
